"use client"

import { CheckCircle } from "@untitledui/icons"
import {
  DialogTrigger as AriaDialogTrigger,
  Heading as AriaHeading,
} from "react-aria-components"

import { BackgroundPattern } from "@/components/ui/background-pattern"
import { Button } from "@/components/ui/button"
import { CloseButton } from "@/components/ui/close-button"
import { FeaturedIcon } from "@/components/ui/featured-icon"
import { Dialog, Modal, ModalOverlay } from "@/components/ui/modal"

export interface SuggestionAcceptModalProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
}

/**
 * Confirms an accept before it fires. Accepting fills the collab's
 * playlist slot and has no un-accept action, so this is a deliberate
 * second click rather than a bare button beside Reject.
 */
export function SuggestionAcceptModal({
  isOpen,
  onOpenChange,
  onConfirm,
}: SuggestionAcceptModalProps) {
  const handleConfirm = () => {
    onConfirm()
    onOpenChange(false)
  }

  const handleCancel = () => onOpenChange(false)

  return (
    <AriaDialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      <ModalOverlay isDismissable>
        <Modal>
          <Dialog>
            <div className="bg-primary relative w-full overflow-hidden rounded-2xl shadow-xl sm:max-w-100">
              <CloseButton
                onClick={handleCancel}
                theme="light"
                size="lg"
                className="absolute top-3 right-3"
              />
              <div className="flex flex-col gap-4 px-4 pt-5 sm:px-6 sm:pt-6">
                <div className="relative w-max">
                  <FeaturedIcon
                    color="success"
                    size="lg"
                    theme="light"
                    icon={CheckCircle}
                  />
                  <BackgroundPattern
                    pattern="circle"
                    size="sm"
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                  />
                </div>
                <div className="z-10 flex flex-col gap-0.5">
                  <AriaHeading
                    slot="title"
                    className="text-md text-primary font-semibold"
                  >
                    Accept this suggestion?
                  </AriaHeading>
                  <p className="text-tertiary text-sm">
                    It fills this prompt&rsquo;s slot in the collab&rsquo;s
                    playlist. There is no un-accept — reject the others instead
                    if you change your mind.
                  </p>
                </div>
              </div>
              <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                <Button color="secondary" size="lg" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button color="primary" size="lg" onClick={handleConfirm}>
                  Accept
                </Button>
              </div>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </AriaDialogTrigger>
  )
}
